<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

  import type { DateFrom } from '../model/types';
  import { useDateFrom } from '../model/useDateFrom';
  import DateFromDropdown from './DateFromDropdown.vue';
  import { DATE_FROM_TEXTS } from '../config/texts';

  interface DateFromFieldProps {
    modelValue?: DateFrom | null;
  }

  const props = withDefaults(defineProps<DateFromFieldProps>(), {
    modelValue: null,
  });

  interface DateFromFieldEmits {
    (e: 'update:modelValue', value: DateFrom): void;
    (e: 'change', value: DateFrom): void;
    (e: 'manual'): void;
  }

  const emit = defineEmits<DateFromFieldEmits>();

  const core = useDateFrom({ initial: props.modelValue });

  const rootRef = ref<HTMLElement | null>(null);

  const onToggle = (): void => {
    core.toggle();
  };

  const onOutsidePointer = (event: MouseEvent): void => {
    const root = rootRef.value;
    const target = event.target as Node | null;
    if (root && target && !root.contains(target)) {
      core.close();
    }
  };

  const onDateClick = (data: string) => {
    core.setAnchorFromISO(data);
    emit('manual');
  };

  const onSelectFlexible = (data: number) => {
    core.setFlexible(data);
    emit('manual');
  } 

  watch(core.controlActivated, (active) => {
    if (active) {
      document.addEventListener('mousedown', onOutsidePointer);
    } else {
      document.removeEventListener('mousedown', onOutsidePointer);
    }
  });

  onBeforeUnmount(() => {
    document.removeEventListener('mousedown', onOutsidePointer);
  });

  watch(core.selection, (value) => {
    emit('update:modelValue', value);
    emit('change', value);
  });

  watch(
    () => props.modelValue,
    (next) => {
      if (!next) {
        return;
      }
      const current = core.selection.value;
      if (
        next.from !== current.from ||
        next.to !== current.to ||
        next.daysFlexible !== current.daysFlexible
      ) {
        core.restore(next);
      }
    },
  );

  onMounted(() => {
    core.restore(props.modelValue);
  });

  defineExpose({
    validate: (): boolean => core.validate(),
  });
</script>

<template>
  <div ref="rootRef" class="date-from-field" data-testid="date-from-field">
    <DateFromDropdown
      :anchor-iso="core.anchorIso.value"
      :flexible-for-calendar="core.flexibleForCalendar.value"
      :flexible="core.flexible.value"
      :min-date="core.minDate.value"
      :max-date="core.maxDate.value"
      :visible="core.controlActivated.value"
      @date-click="onDateClick"
      @select-flexible="onSelectFlexible"
      @close="core.close"
    >
      <template #reference>
        <div
          class="date-from-field__control"
          :class="{ 'date-from-field__control_active': core.controlActivated.value }"
          @click="onToggle"
        >
          <Typo
            class="date-from-field__label"
            :variant="TypoVariant.BodySRegular"
            tag="span"
          >{{ DATE_FROM_TEXTS.fieldLabel }}</Typo>

          <div class="date-from-field__value" data-testid="date-from-value">
            <Typo :variant="TypoVariant.BodyLRegular" tag="span">{{ core.valueLabel.value }}</Typo>
          </div>
        </div>
      </template>
    </DateFromDropdown>
  </div>
</template>

<style scoped lang="scss">
  .date-from-field {
    position: relative;
    width: 100%;

    &__control {
      background: var(--color-bg-primary);
      box-sizing: border-box;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxxs);
      height: 54px;
      justify-content: center;
      padding: var(--spacing-xs) var(--spacing-s);
      transition: background-color var(--transition-default);

      &_active {
        background: var(--color-disabled-100);
      }
    }

    &__label {
      color: var(--color-text-secondary);
      pointer-events: none;
      user-select: none;
    }

    &__value {
      color: var(--color-text-primary);
    }
  }
</style>
